<script lang="ts">
import { RouteNames } from "@/utils/constants.ts";

export function getRouteXAccountsCryptoSend() {
  return {
    name: RouteNames.X_SEND_CRYPTO,
  };
}
</script>
<script setup lang="ts">
import BreadCrumbs from "@/components/BreadCrumbs.vue";
import BasicCard from "@/components/BasicCard.vue";
import TextInput from "@/components/form/TextInput.vue";
import InputSelect from "@/components/form/InputSelect.vue";
import SubmitButton from "@/components/form/SubmitButton.vue";
import { ref, computed, watch } from "vue";
import { 
  ArrowRight as ArrowRightIcon,
  Wallet as WalletIcon,
  QrCode as QrCodeIcon,
  AlertTriangle as AlertTriangleIcon,
  Check as CheckIcon,
  Copy as CopyIcon
} from "lucide-vue-next";

// Mock crypto accounts data
const cryptoAccounts = {
  "btc": "Bitcoin (BTC) - $12,450.00",
  "eth": "Ethereum (ETH) - $8,320.50",
  "usdt": "Tether (USDT) - $5,000.00",
  "usdc": "USD Coin (USDC) - $3,250.00",
  "ada": "Cardano (ADA) - $890.25"
};

// Form state
const selectedAccount = ref("btc");
const recipientAddress = ref("");
const amount = ref("");
const memo = ref("");
const networkFee = ref("0.0002");
const priority = ref("medium");

// UI state
const step = ref(1); // 1: form, 2: review, 3: confirmation
const isLoading = ref(false);
const transactionHash = ref("");
const showQrScanner = ref(false);

// Priority options
const priorityOptions = {
  "low": "Low (Slow, ~30 min) - $0.50",
  "medium": "Medium (Standard, ~10 min) - $1.20",
  "high": "High (Fast, ~5 min) - $2.80"
};

// Validation
const errors = ref({
  recipientAddress: "",
  amount: ""
});

const isValidAddress = computed(() => {
  if (!recipientAddress.value) return false;
  // Basic validation for crypto addresses (simplified)
  const address = recipientAddress.value.trim();
  if (selectedAccount.value === "btc") {
    return /^[13][a-km-zA-HJ-NP-Z1-9]{25,34}$|^bc1[a-z0-9]{39,59}$/.test(address);
  } else if (selectedAccount.value === "eth") {
    return /^0x[a-fA-F0-9]{40}$/.test(address);
  }
  return address.length > 20; // Generic fallback
});

const isValidAmount = computed(() => {
  const amountNum = parseFloat(amount.value);
  return !isNaN(amountNum) && amountNum > 0 && amountNum <= getMaxAmount();
});

const canProceed = computed(() => {
  return isValidAddress.value && isValidAmount.value && !isLoading.value;
});

// Helper functions
const getMaxAmount = () => {
  const balances = {
    "btc": 0.45,
    "eth": 2.8,
    "usdt": 5000,
    "usdc": 3250,
    "ada": 1250
  };
  return balances[selectedAccount.value as keyof typeof balances] || 0;
};

const getCurrencySymbol = () => {
  const symbols = {
    "btc": "BTC",
    "eth": "ETH", 
    "usdt": "USDT",
    "usdc": "USDC",
    "ada": "ADA"
  };
  return symbols[selectedAccount.value as keyof typeof symbols] || "";
};

const calculateUSDValue = () => {
  const rates = {
    "btc": 45000,
    "eth": 3200,
    "usdt": 1,
    "usdc": 1,
    "ada": 0.85
  };
  const rate = rates[selectedAccount.value as keyof typeof rates] || 0;
  const amountNum = parseFloat(amount.value) || 0;
  return (amountNum * rate).toFixed(2);
};

const calculateNetworkFee = () => {
  const fees = {
    "low": 0.0001,
    "medium": 0.0002,
    "high": 0.0005
  };
  return fees[priority.value as keyof typeof fees] || 0.0002;
};

// Watch for priority changes to update network fee
watch(priority, () => {
  networkFee.value = calculateNetworkFee().toString();
});

// Form handlers
const setMaxAmount = () => {
  amount.value = getMaxAmount().toString();
};

const scanQRCode = () => {
  showQrScanner.value = true;
  // Mock QR scan result
  setTimeout(() => {
    recipientAddress.value = selectedAccount.value === "btc" 
      ? "1A1zP1eP5QGefi2DMPTfTL5SLmv7DivfNa"
      : "0x742d35Cc6631C0532925a3b8D43A8Fb33A39d2af";
    showQrScanner.value = false;
  }, 2000);
};

const validateForm = () => {
  errors.value = { recipientAddress: "", amount: "" };
  
  if (!isValidAddress.value) {
    errors.value.recipientAddress = "Please enter a valid recipient address";
  }
  
  if (!isValidAmount.value) {
    errors.value.amount = "Please enter a valid amount";
  }
  
  return isValidAddress.value && isValidAmount.value;
};

const proceedToReview = async () => {
  if (validateForm()) {
    step.value = 2;
  }
};

const confirmTransaction = async () => {
  isLoading.value = true;
  
  // Simulate API call
  setTimeout(() => {
    transactionHash.value = "0x" + Math.random().toString(16).substring(2, 66);
    step.value = 3;
    isLoading.value = false;
  }, 3000);
};

const goBack = () => {
  if (step.value > 1) {
    step.value--;
  }
};

const startNewTransaction = () => {
  step.value = 1;
  recipientAddress.value = "";
  amount.value = "";
  memo.value = "";
  transactionHash.value = "";
  errors.value = { recipientAddress: "", amount: "" };
};

const copyTransactionHash = () => {
  navigator.clipboard.writeText(transactionHash.value);
};
</script>

<template>
  <BreadCrumbs :title="$t('Send Crypto')" class="mb-5" />

  <div class="max-w-2xl mx-auto">
    <!-- Step Indicator -->
    <div class="flex justify-center mb-8">
      <div class="flex items-center space-x-4">
        <div class="flex items-center">
          <div :class="[step >= 1 ? 'bg-blue-500 text-white' : 'bg-gray-300 text-gray-600', 'w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium']">
            1
          </div>
          <span class="ml-2 text-sm">Send Details</span>
        </div>
        <arrow-right-icon class="h-4 w-4 text-gray-400" />
        <div class="flex items-center">
          <div :class="[step >= 2 ? 'bg-blue-500 text-white' : 'bg-gray-300 text-gray-600', 'w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium']">
            2
          </div>
          <span class="ml-2 text-sm">Review</span>
        </div>
        <arrow-right-icon class="h-4 w-4 text-gray-400" />
        <div class="flex items-center">
          <div :class="[step >= 3 ? 'bg-green-500 text-white' : 'bg-gray-300 text-gray-600', 'w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium']">
            <check-icon v-if="step >= 3" class="h-4 w-4" />
            <span v-else>3</span>
          </div>
          <span class="ml-2 text-sm">Confirm</span>
        </div>
      </div>
    </div>

    <!-- Step 1: Send Form -->
    <BasicCard v-if="step === 1">
      <div class="space-y-6">
        <div class="flex items-center gap-3 mb-6">
          <wallet-icon class="h-6 w-6 text-blue-600" />
          <h2 class="text-xl font-semibold">Send Crypto</h2>
        </div>

        <form @submit.prevent="proceedToReview" class="space-y-6">
          <!-- From Account -->
          <div>
            <label class="text-sm text-gray-600 mb-2 block">From Account</label>
            <InputSelect 
              v-model="selectedAccount" 
              :list="cryptoAccounts"
              placeholder="Select account"
            />
            <p class="text-sm text-gray-500 mt-1">
              Available: {{ getMaxAmount() }} {{ getCurrencySymbol() }}
            </p>
          </div>

          <!-- Recipient Address -->
          <div>
            <label class="text-sm text-gray-600 mb-2 block">Recipient Address</label>
            <div class="flex gap-2">
              <TextInput
                v-model="recipientAddress"
                name="recipientAddress"
                type="text"
                placeholder="Enter wallet address"
                :errors="errors.recipientAddress ? [errors.recipientAddress] : null"
                :isError="!!errors.recipientAddress"
                class="flex-1"
              />
              <button
                type="button"
                @click="scanQRCode"
                class="px-4 py-2 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                :disabled="showQrScanner"
              >
                <qr-code-icon class="h-5 w-5" />
              </button>
            </div>
            <div v-if="showQrScanner" class="mt-2 p-4 bg-blue-50 rounded-lg text-center">
              <div class="animate-pulse">Scanning QR Code...</div>
            </div>
          </div>

          <!-- Amount -->
          <div>
            <label class="text-sm text-gray-600 mb-2 block">Amount</label>
            <div class="flex gap-2">
              <TextInput
                v-model="amount"
                name="amount"
                type="number"
                :placeholder="`Enter amount in ${getCurrencySymbol()}`"
                :errors="errors.amount ? [errors.amount] : null"
                :isError="!!errors.amount"
                class="flex-1"
              />
              <button
                type="button"
                @click="setMaxAmount"
                class="px-4 py-2 bg-blue-100 text-blue-600 rounded-lg hover:bg-blue-200 transition-colors"
              >
                MAX
              </button>
            </div>
            <p v-if="amount && !isNaN(parseFloat(amount))" class="text-sm text-gray-500 mt-1">
              ≈ ${{ calculateUSDValue() }} USD
            </p>
          </div>

          <!-- Network Fee Priority -->
          <div>
            <label class="text-sm text-gray-600 mb-2 block">Network Fee</label>
            <InputSelect 
              v-model="priority" 
              :list="priorityOptions"
              placeholder="Select priority"
            />
          </div>

          <!-- Memo (Optional) -->
          <div>
            <label class="text-sm text-gray-600 mb-2 block">Memo (Optional)</label>
            <TextInput
              v-model="memo"
              name="memo"
              type="text"
              placeholder="Add a note for this transaction"
            />
          </div>

          <!-- Action Buttons -->
          <div class="flex justify-end pt-4">
            <SubmitButton
              :click="proceedToReview"
              :disabled="!canProceed"
              class="px-8 py-3"
            >
              Review Transaction
            </SubmitButton>
          </div>
        </form>
      </div>
    </BasicCard>

    <!-- Step 2: Review Transaction -->
    <BasicCard v-if="step === 2">
      <div class="space-y-6">
        <div class="flex items-center gap-3 mb-6">
          <alert-triangle-icon class="h-6 w-6 text-orange-600" />
          <h2 class="text-xl font-semibold">Review Transaction</h2>
        </div>

        <div class="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
          <div class="flex items-start gap-3">
            <alert-triangle-icon class="h-5 w-5 text-yellow-600 mt-0.5" />
            <div>
              <h3 class="font-medium text-yellow-800 mb-1">Important Notice</h3>
              <p class="text-sm text-yellow-700">
                Please verify all details carefully. Cryptocurrency transactions are irreversible.
              </p>
            </div>
          </div>
        </div>

        <div class="space-y-4">
          <div class="grid grid-cols-2 gap-4 p-4 bg-gray-50 rounded-lg">
            <div>
              <p class="text-sm text-gray-600">From</p>
              <p class="font-medium">{{ cryptoAccounts[selectedAccount as keyof typeof cryptoAccounts] }}</p>
            </div>
          </div>

          <div class="p-4 bg-gray-50 rounded-lg">
            <p class="text-sm text-gray-600 mb-1">Recipient Address</p>
            <p class="font-mono text-sm break-all">{{ recipientAddress }}</p>
          </div>

          <div class="grid grid-cols-2 gap-4">
            <div class="p-4 bg-gray-50 rounded-lg">
              <p class="text-sm text-gray-600 mb-1">Amount</p>
              <p class="font-semibold text-lg">{{ amount }} {{ getCurrencySymbol() }}</p>
              <p class="text-sm text-gray-500">≈ ${{ calculateUSDValue() }} USD</p>
            </div>
            <div class="p-4 bg-gray-50 rounded-lg">
              <p class="text-sm text-gray-600 mb-1">Network Fee</p>
              <p class="font-medium">{{ networkFee }} {{ getCurrencySymbol() }}</p>
            </div>
          </div>

          <div class="p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <p class="text-sm text-blue-600 mb-1">Total Amount</p>
            <p class="font-semibold text-lg text-blue-800">
              {{ (parseFloat(amount) + parseFloat(networkFee)).toFixed(8) }} {{ getCurrencySymbol() }}
            </p>
          </div>

          <div v-if="memo" class="p-4 bg-gray-50 rounded-lg">
            <p class="text-sm text-gray-600 mb-1">Memo</p>
            <p>{{ memo }}</p>
          </div>
        </div>

        <div class="flex justify-between pt-6">
          <button
            @click="goBack"
            class="px-6 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
          >
            Back
          </button>
          <SubmitButton
            :click="confirmTransaction"
            :disabled="isLoading"
            class="px-8 py-3"
          >
            <span v-if="isLoading">Processing...</span>
            <span v-else>Confirm & Send</span>
          </SubmitButton>
        </div>
      </div>
    </BasicCard>

    <!-- Step 3: Confirmation -->
    <BasicCard v-if="step === 3">
      <div class="text-center space-y-6">
        <div class="flex justify-center">
          <div class="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
            <check-icon class="h-8 w-8 text-green-600" />
          </div>
        </div>

        <div>
          <h2 class="text-2xl font-semibold text-green-800 mb-2">Transaction Sent!</h2>
          <p class="text-gray-600">
            Your crypto has been sent successfully. It may take a few minutes to appear in the recipient's wallet.
          </p>
        </div>

        <div class="bg-gray-50 p-4 rounded-lg">
          <p class="text-sm text-gray-600 mb-2">Transaction Hash</p>
          <div class="flex items-center gap-2">
            <code class="text-sm font-mono bg-white p-2 rounded border flex-1 break-all">
              {{ transactionHash }}
            </code>
            <button
              @click="copyTransactionHash"
              class="p-2 bg-blue-100 text-blue-600 rounded hover:bg-blue-200 transition-colors"
            >
              <copy-icon class="h-4 w-4" />
            </button>
          </div>
        </div>

        <div class="space-y-2">
          <p class="font-medium">{{ amount }} {{ getCurrencySymbol() }} sent</p>
          <p class="text-sm text-gray-600">Network fee: {{ networkFee }} {{ getCurrencySymbol() }}</p>
        </div>

        <div class="flex justify-center gap-4 pt-6">
          <button
            @click="startNewTransaction"
            class="px-6 py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
          >
            Send Another
          </button>
          <button
            @click="$router.push({ name: 'account.crypto' })"
            class="px-6 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
          >
            Back to Crypto
          </button>
        </div>
      </div>
    </BasicCard>
  </div>
</template>

<style scoped lang="scss">

</style>
